import { useEffect, useRef, useState } from 'react';
import { useGetEmbedProductsQuery } from '../../../api';
import { EmbedProductConfig } from '../../utils/urlGenerator';

/**
 * Product Multi-Select Component
 *
 * Allows partners to select one or more products for their embed.
 * Selected products appear as chips; clicking ★ on a chip sets it as the default.
 * The default product is shown first in the embed's "Select Product" tab.
 */

interface ProductMultiSelectProps {
  value: EmbedProductConfig[];
  onChange: (products: EmbedProductConfig[]) => void;
  partnerKey: string;
}

export default function ProductMultiSelect({
  value,
  onChange,
  partnerKey
}: ProductMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    data: products,
    isLoading,
    isError
  } = useGetEmbedProductsQuery({ refersionId: partnerKey });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const isSelected = (productTag: string) =>
    value.some(p => p.productTag === productTag);

  const handleToggle = (productTag: string, displayName: string) => {
    if (isSelected(productTag)) {
      const remaining = value.filter(p => p.productTag !== productTag);
      const wasDefault = value.find(p => p.productTag === productTag)
        ?.isDefault;
      // If the removed product was the default, promote the first remaining one
      if (wasDefault && remaining.length > 0) {
        onChange([{ ...remaining[0], isDefault: true }, ...remaining.slice(1)]);
      } else {
        onChange(remaining);
      }
    } else {
      const newProduct: EmbedProductConfig = {
        productTag,
        productTitle: displayName,
        // First product added automatically becomes the default
        isDefault: value.length === 0,
        allowedStyles: [],
        allowedFonts: []
      };
      onChange([...value, newProduct]);
    }
  };

  const handleSetDefault = (e: React.MouseEvent, productTag: string) => {
    e.stopPropagation();
    onChange(
      value.map(p => ({ ...p, isDefault: p.productTag === productTag }))
    );
  };

  if (isLoading) {
    return (
      <div className="mb-6">
        <label className="mb-2 block text-sm font-semibold text-[#014876]">
          Products
        </label>
        <div className="box-border w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          Loading products...
        </div>
      </div>
    );
  }

  if (isError || !products || products.length === 0) {
    return (
      <div className="mb-6">
        <label className="mb-2 block text-sm font-semibold text-[#014876]">
          Products
        </label>
        <p className="mb-2 text-xs text-red-500">
          Error loading products, please try again
        </p>
        <div className="box-border w-full rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
          Please email fundraising@namebubbles.com to complete your partner
          setup.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" ref={dropdownRef}>
      <label className="mb-2 block text-sm font-semibold text-[#014876]">
        Products
      </label>
      <p className="mb-2 text-xs text-gray-500">
        Select one or more products. For multi-product embeds, click{' '}
        <span className="text-yellow-500">★</span> on a selected product to set
        it as the default shown first.
      </p>

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#014876]"
        >
          {value.length === 0 ? (
            <span className="text-gray-400">Select products...</span>
          ) : (
            <span className="text-gray-700">
              {value.length} product{value.length !== 1 ? 's' : ''} selected —
              click to add or remove
            </span>
          )}
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
            <div className="py-1">
              {products.map(product => {
                const selected = isSelected(product.product_tag);
                const selectedConfig = value.find(
                  p => p.productTag === product.product_tag
                );
                return (
                  <div
                    key={product.product_id}
                    className="flex cursor-pointer items-center px-3 py-2 hover:bg-gray-100"
                  >
                    {/* Checkbox — toggles selection */}
                    <label className="flex flex-1 cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() =>
                          handleToggle(
                            product.product_tag,
                            product.display_name
                          )
                        }
                        className="rounded border-gray-300 text-[#014876] focus:ring-[#014876]"
                      />
                      <span className="text-sm text-gray-700">
                        {product.display_name}
                      </span>
                    </label>

                    {/* Star — only visible for selected products; sets as default */}
                    {selected && (
                      <button
                        type="button"
                        title={
                          selectedConfig?.isDefault
                            ? 'Default product'
                            : 'Set as default'
                        }
                        onClick={e => handleSetDefault(e, product.product_tag)}
                        className={`ml-2 shrink-0 text-base leading-none transition-colors ${
                          selectedConfig?.isDefault
                            ? 'text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-400'
                        }`}
                      >
                        ★
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {value.length > 1 && (
        <p className="mt-1 text-xs text-gray-400">
          ★ = default product loaded first in the embed
        </p>
      )}
    </div>
  );
}
